import React, { useState } from "react";
import { Modal, Button, Card, Row, Col, Spinner } from "react-bootstrap";
import axios from "axios";

const CandidateDetailModal = ({ show, onHide, candidate, refreshData }) => {
  const [deleting, setDeleting] = useState(false);

  if (!candidate) return null;

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "N/A";

  const formatTime = (date) =>
    date ? new Date(date).toLocaleTimeString("en-GB") : "N/A";

  // 🗑️ Delete audio function
  const handleDeleteAudio = async () => {
    if (!candidate.phoneNumber) return alert("Phone number missing");
    if (
      !window.confirm(
        "Are you sure you want to delete this interview and its audio file?",
      )
    )
      return;

    try {
      setDeleting(true);
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/v1/interviews/${candidate.phoneNumber}/audio`,
      );
      alert("Interview and audio deleted successfully!");
      setDeleting(false);
      onHide();
      if (refreshData) refreshData(); // Refresh list after delete
    } catch (error) {
      console.error(error);
      alert("Failed to delete interview/audio");
      setDeleting(false);
    }
  };

  // 🎨 Helper for color-coded score
  const getScoreColor = (score) => {
    if (score >= 8) return "text-success";
    if (score >= 5) return "text-warning";
    return "text-danger";
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Candidate Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* 🧍 Candidate Info */}
        <Card className="mb-3 shadow-sm">
          <Card.Body>
            <Card.Title className="mb-3">
              <u>Candidate Information</u>{" "}
            </Card.Title>
            <Row className="mb-2">
              <Col md={12}>
                <strong>Job ID:</strong>{" "}
                <span
                  style={{
                    wordBreak: "break-all",
                    background: "#f8f9fa",
                    padding: "4px 8px",
                    borderRadius: "6px",
                    display: "inline-block",
                    fontSize: "13px",
                  }}
                >
                  {candidate.jobId?.jobId || "-"}
                </span>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <strong>Candidate ID:</strong> {candidate.candidateId}
              </Col>
              <Col md={6}>
                <strong>Name:</strong> {candidate.name}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <strong>Contact:</strong> {candidate.phoneNumber || "-"}
              </Col>

              <Col md={6}>
                <strong>Email:</strong> {candidate.email || "-"}
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={6}>
                <strong>Date Applied:</strong> {formatDate(candidate.date)}
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={6}>
                <strong>Aadhar-Card Number:</strong> {candidate.aadharNumber}
              </Col>
              <Col md={6}>
                <strong>Driving License :</strong> {candidate.licenseNumber}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <strong>Resume:</strong>{" "}
                {candidate.resume?.url ? (
                  <a
                    href={candidate.resume.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary ms-2"
                  >
                    View Resume
                  </a>
                ) : (
                  "-"
                )}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <strong>Job Title:</strong> {candidate.jobId?.title || "-"}
              </Col>
              <Col md={6}>
                <strong>City:</strong> {candidate.jobId?.city || "-"}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <strong>Supervisor/Reporting Manager:</strong>{" "}
                {candidate.userId?.name || "-"}
              </Col>

              <Col md={6}>
                <strong>Progress:</strong> {candidate.progress}
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={6}>
                <strong>Status:</strong>{" "}
                <span
                  className={
                    candidate.status === "In-Progress"
                      ? "text-success"
                      : "text-primary"
                  }
                >
                  {candidate.status}
                </span>
              </Col>
              <Col md={6}>
                <strong>Approved:</strong> {candidate.approved}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <strong>Description:</strong> {candidate.description}
              </Col>
              <Col md={6}>
                <strong>Comments:</strong> {candidate.comments || "-"}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* 🎤 Interview Info */}
        {candidate.interviewData && (
          <Card className="mb-3 shadow-sm border-primary">
            <Card.Body>
              <Card.Title className="mb-3 d-flex justify-content-between align-items-center">
                <u>Interview Details</u>
                {candidate.interviewData.audioUrl && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDeleteAudio}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Deleting...
                      </>
                    ) : (
                      "Delete Audio"
                    )}
                  </Button>
                )}
              </Card.Title>

              <Row className="mb-2">
                <Col md={6}>
                  <strong>Interview Date:</strong>{" "}
                  {formatDate(candidate.interviewData.interviewDate)}{" "}
                  {formatTime(candidate.interviewData.interviewDate)}
                </Col>
                <Col md={6}>
                  <strong>Language:</strong> {candidate.interviewData.language}
                </Col>
              </Row>

              {/* 🟣 Score & Reason Section */}
              {(candidate.interviewData.score ||
                candidate.interviewData.reason) && (
                <Row className="mb-3">
                  <Col md={6}>
                    <div
                      className="p-3 bg-light border rounded shadow-sm"
                      style={{ borderLeft: "4px solid #198754" }}
                    >
                      <h6 className="mb-1">
                        🎯 <strong>Score:</strong>{" "}
                        <span
                          className={getScoreColor(
                            candidate.interviewData.score,
                          )}
                        >
                          {candidate.interviewData.score ?? "N/A"}
                        </span>
                      </h6>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div
                      className="p-3 bg-light border rounded shadow-sm"
                      style={{ borderLeft: "4px solid #0d6efd" }}
                    >
                      <h6 className="mb-1 text-primary">
                        💡 <strong>Reason:</strong>
                      </h6>
                      <p className="mb-0 text-muted">
                        {candidate.interviewData.reason || "No reason provided"}
                      </p>
                    </div>
                  </Col>
                </Row>
              )}

              {candidate.interviewData.audioUrl && (
                <Row className="mb-2">
                  <Col>
                    <strong>Audio:</strong>
                    <audio
                      controls
                      src={candidate.interviewData.audioUrl}
                      className="d-block mt-1 w-100"
                    >
                      Your browser does not support the audio element.
                    </audio>
                  </Col>
                </Row>
              )}

              {candidate.interviewData.chats?.length > 0 && (
                <>
                  <strong>Chats:</strong>
                  <div className="mt-2">
                    {candidate.interviewData.chats.map((chat, idx) => (
                      <Card
                        key={chat._id || idx}
                        className="mb-2 shadow-sm"
                        style={{ borderLeft: "4px solid #4723d9" }}
                      >
                        <Card.Body>
                          <Row>
                            <Col md={6}>
                              <p className="mb-1">
                                <strong>❓ Question:</strong> {chat.question}
                              </p>
                            </Col>
                            <Col md={6}>
                              <p className="mb-0">
                                <strong>💬 Answer:</strong> {chat.answer}
                              </p>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CandidateDetailModal;
